import { useMemo } from "react";
import { router } from "@inertiajs/react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";

interface Column {
  key: string;
  label: string;
  type?: string;
  options?: Array<{ value: string; label: string; color?: string; class?: string }>;
}

interface Props {
  columns: Column[];
  records: Array<Record<string, unknown>>;
  showPath?: string;
  selectedIds: Array<string | number>;
  onSelectionChange: (ids: Array<string | number>) => void;
  emptyLabel?: string;
}

export default function ResourceTable({
  columns,
  records,
  showPath,
  selectedIds,
  onSelectionChange,
  emptyLabel,
}: Props) {
  const enumPillClass = (option?: { color?: string; class?: string }) => {
    if (option?.class) return option.class;

    const base = "inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium";
    switch (option?.color) {
      case "green":
        return `${base} bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-900/40`;
      case "red":
        return `${base} bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-900/40`;
      case "yellow":
        return `${base} bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-900/40`;
      case "blue":
        return `${base} bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-900/30 dark:text-sky-200 dark:border-sky-900/40`;
      case "orange":
        return `${base} bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-200 dark:border-orange-900/40`;
      case "gray":
      default:
        return `${base} bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800/50 dark:text-slate-200 dark:border-slate-700`;
    }
  };

  const formatValue = (column: Column, value: unknown) => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "boolean") return value ? "نعم" : "لا";

    if (column.type === "enum" && column.options) {
      const match = column.options.find((option) => option.value === String(value));
      return match ? <span className={enumPillClass(match)}>{match.label}</span> : String(value);
    }

    if (typeof value === "string" && column.key.endsWith("_at")) {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = String(date.getFullYear());
        return `${year}-${month}-${day}`;
      }
    }

    return String(value);
  };

  const canOpen = Boolean(showPath);
  const recordIds = useMemo(
    () => records.map((record) => record.id).filter((id) => id !== undefined),
    [records]
  );
  const allSelected = recordIds.length > 0 && selectedIds.length === recordIds.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) {
      onSelectionChange([]);
      return;
    }
    onSelectionChange(recordIds as Array<string | number>);
  };

  const toggleSelectRow = (id: string | number) => {
    onSelectionChange(
      selectedIds.includes(id) ? selectedIds.filter((value) => value !== id) : [...selectedIds, id]
    );
  };

  if (records.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">{emptyLabel || "لا توجد بيانات"}</div>
    );
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[48px]">
              <div
                className="flex items-center justify-center"
                onClick={(event) => event.stopPropagation()}
              >
                <Checkbox
                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={toggleSelectAll}
                />
              </div>
            </TableHead>
            {columns.map((column) => (
              <TableHead key={column.key}>{column.label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((record) => {
            const recordId = record.id as string | number | undefined;
            const rowPath =
              recordId && showPath ? showPath.replace("__ID__", String(recordId)) : "";
            const isSelected = recordId ? selectedIds.includes(recordId) : false;

            return (
              <TableRow
                key={recordId ?? JSON.stringify(record)}
                className={canOpen ? "cursor-pointer hover:bg-muted/60" : undefined}
                onClick={() => {
                  if (!rowPath) return;
                  router.get(rowPath);
                }}
              >
                <TableCell
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <div className="flex items-center justify-center">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => {
                        if (!recordId) return;
                        toggleSelectRow(recordId);
                      }}
                    />
                  </div>
                </TableCell>
                {columns.map((column) => (
                  <TableCell key={`${recordId}-${column.key}`}>
                    {formatValue(column, record[column.key])}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
